import { useAuth } from '@clerk/clerk-react';
import { createFileRoute } from '@tanstack/react-router';
import {
  BookOpen,
  CheckCircle2,
  Clock,
  GraduationCap,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useMyTrainingRecords,
  useUpdateMyAssignment,
} from '@/features/workforce-readiness/api/use-training-records';
import { useProfile } from '@/features/profile/api/use-profile';
import type { TrainingRecord } from '@/features/workforce-readiness/data';
import { mapTrainingRecordFromApi } from '@/features/workforce-readiness/mappers';

export const Route = createFileRoute(
  '/_authenticated/workforce-readiness/my-trainings'
)({
  component: MyTrainingsPage,
});

type StatusFilter = 'all' | 'not_started' | 'in_progress' | 'completed';

const STATUS_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  completed: 'default',
  in_progress: 'secondary',
  not_started: 'outline',
};

const TRAINING_STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  completed: 'default',
  active: 'secondary',
  planned: 'outline',
  archived: 'destructive',
};

function getMyAssignment(record: TrainingRecord, userId: string) {
  return record.assignedEmployees.find((a) => a.userId === userId);
}

function MyTrainingsPage() {
  const { t } = useTranslation('common');
  const { userId: clerkUserId } = useAuth();
  const { data: profile } = useProfile();
  const currentUserId = profile?.id ?? null;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [completeDialog, setCompleteDialog] = useState<TrainingRecord | null>(null);

  const { records, isLoading } = useMyTrainingRecords({
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const { updateAssignment, isUpdating } = useUpdateMyAssignment();
  const items = useMemo(
    () => records?.items.map(mapTrainingRecordFromApi) ?? [],
    [records?.items]
  );

  const filteredRecords = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter((r) => r.trainingName.toLowerCase().includes(q));
  }, [items, searchQuery]);

  // Compute stats
  const stats = useMemo(() => {
    const total = items.length;
    let completed = 0;
    let inProgress = 0;
    let notStarted = 0;

    for (const record of items) {
      const assignment = currentUserId ? getMyAssignment(record, currentUserId) : null;
      if (!assignment) continue;
      if (assignment.completionStatus === 'completed') completed++;
      else if (assignment.completionStatus === 'in_progress') inProgress++;
      else notStarted++;
    }

    return { total, completed, inProgress, notStarted };
  }, [currentUserId, items]);

  const handleMarkComplete = async (record: TrainingRecord) => {
    try {
      await updateAssignment({
        recordId: record.id,
        data: {
          userId: currentUserId ?? clerkUserId ?? '',
          completionStatus: 'completed',
          completionDate: new Date().toISOString().split('T')[0],
        },
      });
      setCompleteDialog(null);
    } catch {
      // Error state is exposed via hook
    }
  };

  const handleMarkInProgress = async (record: TrainingRecord) => {
    try {
      await updateAssignment({
        recordId: record.id,
        data: {
          userId: currentUserId ?? clerkUserId ?? '',
          completionStatus: 'in_progress',
        },
      });
    } catch {
      // Error state is exposed via hook
    }
  };

  const filterOptions = [
    { value: 'all', label: t('workforceReadiness.myTrainings.all') },
    { value: 'not_started', label: t('workforceReadiness.myTrainings.notStarted') },
    { value: 'in_progress', label: t('workforceReadiness.myTrainings.inProgress') },
    { value: 'completed', label: t('workforceReadiness.myTrainings.completed') },
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t('workforceReadiness.myTrainings.pageTitle')}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('workforceReadiness.myTrainings.pageDescription')}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('workforceReadiness.myTrainings.totalAssigned')}
            </CardTitle>
            <GraduationCap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('workforceReadiness.myTrainings.completed')}
            </CardTitle>
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{stats.completed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('workforceReadiness.myTrainings.inProgress')}
            </CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.inProgress}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('workforceReadiness.myTrainings.notStarted')}
            </CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.notStarted}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('workforceReadiness.myTrainings.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); }}
            className="pl-9"
          />
        </div>
        <NativeSelect
          options={filterOptions}
          value={statusFilter}
          onChange={(val) => { setStatusFilter(val as StatusFilter); }}
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          {t('workforceReadiness.myTrainings.loading')}
        </div>
      ) : filteredRecords.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <GraduationCap className="h-12 w-12 mb-4 opacity-50" />
          <p className="text-lg font-medium">
            {t('workforceReadiness.myTrainings.noTrainingsAssigned')}
          </p>
          <p className="text-sm">
            {t('workforceReadiness.myTrainings.noTrainingsDescription')}
          </p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('workforceReadiness.myTrainings.trainingName')}</TableHead>
                <TableHead>{t('workforceReadiness.myTrainings.type')}</TableHead>
                <TableHead>{t('workforceReadiness.myTrainings.date')}</TableHead>
                <TableHead>{t('workforceReadiness.myTrainings.trainingStatus')}</TableHead>
                <TableHead>{t('workforceReadiness.myTrainings.myStatus')}</TableHead>
                <TableHead>{t('workforceReadiness.myTrainings.completionDate')}</TableHead>
                <TableHead className="text-right">{t('workforceReadiness.myTrainings.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRecords.map((record) => {
                const assignment = currentUserId
                  ? getMyAssignment(record, currentUserId)
                  : null;
                const completionStatus = assignment?.completionStatus ?? 'not_started';

                return (
                  <TableRow key={record.id}>
                    <TableCell className="font-medium">{record.trainingName}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{record.trainingType}</Badge>
                    </TableCell>
                    <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Badge variant={TRAINING_STATUS_VARIANT[record.status] ?? 'outline'}>
                        {record.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGE_VARIANT[completionStatus] ?? 'outline'}>
                        {completionStatus === 'not_started'
                          ? t('workforceReadiness.myTrainings.notStarted')
                          : completionStatus === 'in_progress'
                            ? t('workforceReadiness.myTrainings.inProgress')
                            : t('workforceReadiness.myTrainings.completed')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {assignment?.completionDate
                        ? new Date(assignment.completionDate).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {completionStatus === 'not_started' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            void handleMarkInProgress(record);
                          }}
                          disabled={isUpdating}
                        >
                          {t('workforceReadiness.myTrainings.start')}
                        </Button>
                      )}
                      {completionStatus === 'in_progress' && (
                        <Button
                          size="sm"
                          onClick={() => { setCompleteDialog(record); }}
                          disabled={isUpdating}
                        >
                          {t('workforceReadiness.myTrainings.markComplete')}
                        </Button>
                      )}
                      {completionStatus === 'completed' && (
                        <span className="text-emerald-600 text-sm font-medium flex items-center justify-end gap-1">
                          <CheckCircle2 className="h-4 w-4" />
                          {t('workforceReadiness.myTrainings.done')}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Complete confirmation dialog */}
      <Dialog open={!!completeDialog} onOpenChange={(open) => { if (!open) setCompleteDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workforceReadiness.myTrainings.completeTrainingTitle')}</DialogTitle>
            <DialogDescription>
              {t('workforceReadiness.myTrainings.completeTrainingDescription')}
            </DialogDescription>
          </DialogHeader>
          {completeDialog && (
            <div className="py-4">
              <Label className="text-sm font-medium">{t('workforceReadiness.myTrainings.trainingName')}</Label>
              <p className="mt-1 text-sm">{completeDialog.trainingName}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompleteDialog(null); }}>
              {t('workforceReadiness.myTrainings.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (completeDialog) {
                  void handleMarkComplete(completeDialog);
                }
              }}
              disabled={isUpdating}
            >
              {isUpdating
                ? t('workforceReadiness.myTrainings.saving')
                : t('workforceReadiness.myTrainings.confirmComplete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
